import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WhatsAppConversations = ({ token }) => {
  const [conversations, setConversations] = useState([]);
  const [archives, setArchives] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [showBin, setShowBin] = useState(false);
  const [msg, setMsg] = useState(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes] = await Promise.all([
        fetch(`${API_URL}/api/whatsapp/conversations`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/whatsapp/conversations/archive`, { headers: authHeaders() }),
      ]);
      const cData = await cRes.json();
      const aData = await aRes.json();
      if (cData.success) { setConversations(cData.conversations || []); setSelected(new Set()); }
      if (aData.success) { setArchives(aData.archives || []); }
    } catch (e) {
      setMsg({ type: 'error', text: 'Load नहीं हो पाया' });
    }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggle = (phone) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(phone) ? next.delete(phone) : next.add(phone);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === conversations.length ? new Set() : new Set(conversations.map((c) => c.phone))));
  };

  const runDelete = async (payload, confirmText) => {
    if (!window.confirm(confirmText)) return;
    setDeleting(true); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/conversations/delete`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        const d = data.deleted || {};
        setMsg({ type: 'success', text: `✅ हट गया + Aayush memory reset (Recycle Bin में सुरक्षित — Restore हो सकता है) — Sessions: ${d.sessions}, Inquiries: ${d.inquiries}, Leads: ${d.leads}, Logs: ${d.logs}, Alerts: ${d.hot_alerts}, Chat-memory: ${(d.chat_messages || 0)} msgs / ${(d.chat_sessions || 0)} session` });
        await load();
      } else {
        setMsg({ type: 'error', text: data.detail || 'Delete failed' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Delete failed: ' + e.message });
    }
    setDeleting(false);
  };

  const deleteSelected = () => {
    if (selected.size === 0) { setMsg({ type: 'error', text: 'पहले conversations select करें' }); return; }
    runDelete({ phones: Array.from(selected) },
      `${selected.size} conversation(s) हटाई जाएँगी। ये Recycle Bin में सुरक्षित रहेंगी और बाद में Restore की जा सकती हैं।\n\nजारी रखें?`);
  };

  const deleteAll = () => {
    const typed = window.prompt('⚠️ यह सभी WhatsApp bot chats, sessions, inquiries, bot-leads, logs व alerts हटा देगा।\nये Recycle Bin में सुरक्षित रहेंगे (Restore संभव), पर सावधानी ज़रूरी है।\n\nपक्का करने के लिए नीचे box में बड़े अक्षरों में टाइप करें:  DELETE ALL');
    if (typed !== 'DELETE ALL') {
      if (typed !== null) setMsg({ type: 'error', text: 'Confirmation match नहीं हुआ — कुछ नहीं हटाया गया।' });
      return;
    }
    runDelete({ delete_all: true, confirm: true }, 'अंतिम पुष्टि: सभी test chats हटाएँ? (Recycle Bin में सुरक्षित रहेंगे)');
  };

  const restore = async (archive_id) => {
    if (!window.confirm('यह snapshot वापस active conversations में restore कर दिया जाए?')) return;
    setRestoringId(archive_id); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/conversations/restore`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ archive_id }),
      });
      const data = await res.json();
      if (data.success) {
        const r = data.restored || {};
        setMsg({ type: 'success', text: `♻️ Restore हो गया — Sessions: ${r.sessions}, Inquiries: ${r.inquiries}, Leads: ${r.leads}, Logs: ${r.logs}, Alerts: ${r.hot_alerts}` });
        await load();
      } else {
        setMsg({ type: 'error', text: data.detail || 'Restore failed' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Restore failed: ' + e.message });
    }
    setRestoringId(null);
  };

  const allSelected = conversations.length > 0 && selected.size === conversations.length;

  return (
    <div className="bg-white rounded-xl p-5 border" data-testid="wa-conversations-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="font-bold text-gray-800">💬 WhatsApp Conversations ({conversations.length})</h4>
          <p className="text-xs text-gray-500 mt-0.5">Test के पुराने records select करके हटाएँ — number दोबारा "नया" माना जाएगा। <b className="text-emerald-600">हटाया हुआ Recycle Bin में सुरक्षित रहता है, Restore किया जा सकता है।</b></p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowBin((s) => !s)} data-testid="wa-conv-toggle-bin" className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium">♻️ Recycle Bin ({archives.length})</button>
          <button onClick={load} data-testid="wa-conv-refresh" className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">↻ Refresh</button>
        </div>
      </div>

      {msg && (
        <div className={`rounded-lg px-3 py-2 text-sm mb-3 ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="wa-conv-msg">{msg.text}</div>
      )}

      {/* Recycle Bin */}
      {showBin && (
        <div className="mb-4 border-2 border-amber-200 rounded-xl p-3 bg-amber-50/40" data-testid="wa-conv-bin">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-semibold text-amber-800 text-sm">♻️ Recycle Bin — हटाए गए conversations (Restore करें)</h5>
          </div>
          {archives.length === 0 ? (
            <p className="text-xs text-gray-500 py-3 text-center" data-testid="wa-conv-bin-empty">Recycle Bin खाली है — कुछ हटाया नहीं गया।</p>
          ) : (
            <div className="space-y-2">
              {archives.map((a) => (
                <div key={a.archive_id} className="flex items-center gap-3 bg-white rounded-lg border px-3 py-2" data-testid={`wa-conv-archive-${a.archive_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                      {a.scope === 'all' ? '🗂️ सभी chats' : `📱 ${(a.phones || []).join(', ') || 'selected'}`}
                      <span className="ml-2 text-[11px] text-gray-500">({a.total} records)</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Sessions {a.counts?.sessions || 0} · Inquiries {a.counts?.inquiries || 0} · Leads {a.counts?.leads || 0} · Logs {a.counts?.logs || 0} · Alerts {a.counts?.hot_alerts || 0}
                      {a.deleted_at ? ` · ${new Date(a.deleted_at).toLocaleString('en-IN')}` : ''}{a.deleted_by ? ` · ${a.deleted_by}` : ''}
                    </p>
                  </div>
                  <button onClick={() => restore(a.archive_id)} disabled={restoringId === a.archive_id} data-testid={`wa-conv-restore-${a.archive_id}`}
                    className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-40 shrink-0">
                    {restoringId === a.archive_id ? '⏳ Restoring...' : '♻️ Restore'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="wa-conv-select-all" className="w-4 h-4" />
          सभी select करें ({selected.size})
        </label>
        <button onClick={deleteSelected} disabled={deleting || selected.size === 0} data-testid="wa-conv-delete-selected"
          className="ml-auto text-xs px-3 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-40">
          🗑️ Selected हटाएँ ({selected.size})
        </button>
        <button onClick={deleteAll} disabled={deleting} data-testid="wa-conv-delete-all"
          className="text-xs px-3 py-2 rounded-lg bg-white border-2 border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-40">
          ⚠️ सभी test chats हटाएँ
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : conversations.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm" data-testid="wa-conv-empty">कोई conversation नहीं — सब साफ़ है ✅</div>
      ) : (
        <div className="divide-y border rounded-lg overflow-hidden">
          {conversations.map((c) => (
            <label key={c.phone} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer" data-testid={`wa-conv-row-${c.phone}`}>
              <input type="checkbox" checked={selected.has(c.phone)} onChange={() => toggle(c.phone)} data-testid={`wa-conv-check-${c.phone}`} className="w-4 h-4 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-800 text-sm">{c.name || 'Unknown'}</span>
                  <span className="text-gray-400 text-xs">{c.phone}</span>
                  {c.has_session && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">live session</span>}
                  {c.has_memory && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">🧠 Aayush memory</span>}
                  {c.lead_status && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{c.lead_status}</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {c.inquiries} inquiries{c.last_intent ? ` · ${c.last_intent}` : ''}{c.last_activity ? ` · ${new Date(c.last_activity).toLocaleString('en-IN')}` : ''}
                </p>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhatsAppConversations;
